import React, { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { CREATE_SANCTION_RULE } from "./Sanction";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";

export default function CreateSanctionRuleForm({ onCreated, onClose, colors }) {
    const { t } = useTranslation();

    const [name, setName] = useState("");
    const [maxYellow, setMaxYellow] = useState(4);
    const [redSusp, setRedSusp] = useState(1);
    const [yellowSusp, setYellowSusp] = useState(1);
    const [doubleYellowSusp, setDoubleYellowSusp] = useState(1);

    const [resetOnRed, setResetOnRed] = useState(true);
    const [resetOnDoubleYellow, setResetOnDoubleYellow] = useState(true);
    const [resetOnStageChange, setResetOnStageChange] = useState(true);
    const [resetSanctions, setResetSanctions] = useState(false);
    const [overrideAccum, setOverrideAccum] = useState(true);

    const [yellowFine, setYellowFine] = useState("");
    const [redFine, setRedFine] = useState("");
    const [doubleYellowFine, setDoubleYellowFine] = useState("");

    const [uploading, setUploading] = useState(false);
    const [createSanctionRule] = useMutation(CREATE_SANCTION_RULE);

    const ui = useMemo(() => {
        return {
            scrollWrap: {
                maxHeight: "62vh",
                overflowY: "auto",
                paddingRight: 6,
            },

            section: {
                borderRadius: 14,
                padding: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            sectionHeader: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
            },
            sectionTitle: {
                margin: 0,
                fontSize: 13,
                fontWeight: 900,
                color: "#14151a",
                letterSpacing: "0.01em",
            },
            sectionHint: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },

            label: {
                fontSize: 12.5,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                marginBottom: 6,
            },

            input: {
                borderRadius: 12,
                border: `1px solid ${colors.primary}`,
                fontWeight: 800,
                color: "#14151a",
            },

            row2: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            row3: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 12,
            },

            checkRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                cursor: "pointer",
                userSelect: "none",
            },
            checkLeft: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            checkbox: {
                width: 18,
                height: 18,
                marginTop: 2,
                flex: "0 0 auto",
                accentColor: colors.primary,
                cursor: "pointer",
            },
            checkText: {
                margin: 0,
                fontSize: 13,
                fontWeight: 800,
                color: "rgba(20,21,26,0.82)",
                lineHeight: 1.2,
            },
            checkSub: {
                margin: "3px 0 0",
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },

            footer: {
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                paddingTop: 10,
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            btnMuted: {
                background: "rgba(0,0,0,0.02)",
            },
            monosmall: {
                fontVariantNumeric: "tabular-nums",
            },
        };
    }, [colors.primary]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        try {
            const variables = {
                input: {
                    name,
                    maxAccumulatedYellowCards: parseInt(maxYellow),
                    redCardSuspensionGames: parseInt(redSusp),
                    yellowCardSuspensionGames: parseInt(yellowSusp),
                    doubleYellowCardSuspensionGames: parseInt(doubleYellowSusp),
                    resetYellowCardsOnRed: resetOnRed,
                    resetYellowCardsOnStageChange: resetOnStageChange,
                    resetSanctionsOnStageChange: resetSanctions,
                    resetYellowCardsOnDoubleYellow: resetOnDoubleYellow,
                    doubleYellowCardOverridesAccumulation: overrideAccum,
                    yellowCardFine: yellowFine ? parseFloat(yellowFine) : null,
                    redCardFine: redFine ? parseFloat(redFine) : null,
                    doubleYellowCardFine: doubleYellowFine ? parseFloat(doubleYellowFine) : null,
                },
            };

            const { data } = await createSanctionRule({ variables });

            if (data?.createSanctionRule?.sanctionRule) {
                onCreated(data.createSanctionRule.sanctionRule);
                toast.success(t("createSanctionRuleForm.success"));
                onClose();
            }
        } catch (err) {
            console.error(err);
            toast.error(t("createSanctionRuleForm.error"));
        }
        setUploading(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={ui.scrollWrap}>
                <div style={ui.section}>
                    <div style={ui.sectionHeader}>
                        <h4 style={ui.sectionTitle}>{t("createSanctionRuleForm.sections.basics.title")}</h4>
                        <p style={ui.sectionHint}>{t("createSanctionRuleForm.sections.basics.hint")}</p>
                    </div>

                    <div className="form-group" style={{ marginBottom: 12 }}>
                        <label style={ui.label}>{t("createSanctionRuleForm.fields.ruleName")}</label>
                        <input
                            className="form-control"
                            style={ui.input}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("createSanctionRuleForm.fields.ruleNamePlaceholder")}
                            required
                        />
                    </div>

                    <div style={ui.row2}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>
                                {t("createSanctionRuleForm.fields.maxAccumulatedYellowCards")}
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                style={ui.input}
                                value={maxYellow}
                                onChange={(e) => setMaxYellow(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>
                                {t("createSanctionRuleForm.fields.accumulatedYellowSuspensionGames")}
                            </label>
                            <input
                                type="number"
                                className="form-control"
                                style={ui.input}
                                value={yellowSusp}
                                onChange={(e) => setYellowSusp(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div style={ui.section}>
                    <div style={ui.sectionHeader}>
                        <h4 style={ui.sectionTitle}>{t("createSanctionRuleForm.sections.suspensions.title")}</h4>
                        <p style={ui.sectionHint}>{t("createSanctionRuleForm.sections.suspensions.hint")}</p>
                    </div>

                    <div style={ui.row3}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>{t("createSanctionRuleForm.fields.redCardSuspension")}</label>
                            <input
                                type="number"
                                className="form-control"
                                style={ui.input}
                                value={redSusp}
                                onChange={(e) => setRedSusp(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>{t("createSanctionRuleForm.fields.doubleYellowSuspension")}</label>
                            <input
                                type="number"
                                className="form-control"
                                style={ui.input}
                                value={doubleYellowSusp}
                                onChange={(e) => setDoubleYellowSusp(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>{t("createSanctionRuleForm.fields.accumulatedYellowSuspension")}</label>
                            <input
                                type="number"
                                className="form-control"
                                style={ui.input}
                                value={yellowSusp}
                                onChange={(e) => setYellowSusp(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div style={ui.section}>
                    <div style={ui.sectionHeader}>
                        <h4 style={ui.sectionTitle}>{t("createSanctionRuleForm.sections.resets.title")}</h4>
                        <p style={ui.sectionHint}>{t("createSanctionRuleForm.sections.resets.hint")}</p>
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                        <div
                            style={ui.checkRow}
                            onClick={() => setResetOnRed((v) => !v)}
                            role="button"
                            tabIndex={0}
                        >
                            <div style={ui.checkLeft}>
                                <input
                                    type="checkbox"
                                    style={ui.checkbox}
                                    checked={resetOnRed}
                                    onChange={() => setResetOnRed(!resetOnRed)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <p style={ui.checkText}>{t("createSanctionRuleForm.toggles.resetOnRed.title")}</p>
                                    <p style={ui.checkSub}>{t("createSanctionRuleForm.toggles.resetOnRed.hint")}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            style={ui.checkRow}
                            onClick={() => setResetOnDoubleYellow((v) => !v)}
                            role="button"
                            tabIndex={0}
                        >
                            <div style={ui.checkLeft}>
                                <input
                                    type="checkbox"
                                    style={ui.checkbox}
                                    checked={resetOnDoubleYellow}
                                    onChange={() => setResetOnDoubleYellow(!resetOnDoubleYellow)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <p style={ui.checkText}>{t("createSanctionRuleForm.toggles.resetOnDoubleYellow.title")}</p>
                                    <p style={ui.checkSub}>{t("createSanctionRuleForm.toggles.resetOnDoubleYellow.hint")}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            style={ui.checkRow}
                            onClick={() => setOverrideAccum((v) => !v)}
                            role="button"
                            tabIndex={0}
                        >
                            <div style={ui.checkLeft}>
                                <input
                                    type="checkbox"
                                    style={ui.checkbox}
                                    checked={overrideAccum}
                                    onChange={() => setOverrideAccum(!overrideAccum)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <p style={ui.checkText}>{t("createSanctionRuleForm.toggles.overrideAccumulation.title")}</p>
                                    <p style={ui.checkSub}>{t("createSanctionRuleForm.toggles.overrideAccumulation.hint")}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            style={ui.checkRow}
                            onClick={() => setResetOnStageChange((v) => !v)}
                            role="button"
                            tabIndex={0}
                        >
                            <div style={ui.checkLeft}>
                                <input
                                    type="checkbox"
                                    style={ui.checkbox}
                                    checked={resetOnStageChange}
                                    onChange={() => setResetOnStageChange(!resetOnStageChange)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <p style={ui.checkText}>{t("createSanctionRuleForm.toggles.resetOnStageChange.title")}</p>
                                    <p style={ui.checkSub}>{t("createSanctionRuleForm.toggles.resetOnStageChange.hint")}</p>
                                </div>
                            </div>
                        </div>

                        <div
                            style={ui.checkRow}
                            onClick={() => setResetSanctions((v) => !v)}
                            role="button"
                            tabIndex={0}
                        >
                            <div style={ui.checkLeft}>
                                <input
                                    type="checkbox"
                                    style={ui.checkbox}
                                    checked={resetSanctions}
                                    onChange={() => setResetSanctions(!resetSanctions)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div style={{ minWidth: 0 }}>
                                    <p style={ui.checkText}>{t("createSanctionRuleForm.toggles.resetSanctionsOnStageChange.title")}</p>
                                    <p style={ui.checkSub}>{t("createSanctionRuleForm.toggles.resetSanctionsOnStageChange.hint")}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={ui.section}>
                    <div style={ui.sectionHeader}>
                        <h4 style={ui.sectionTitle}>{t("createSanctionRuleForm.sections.fines.title")}</h4>
                        <p style={ui.sectionHint}>{t("createSanctionRuleForm.sections.fines.hint")}</p>
                    </div>

                    <div style={ui.row3}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>{t("createSanctionRuleForm.fields.yellowCardFine")}</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                style={{ ...ui.input, ...ui.monosmall }}
                                value={yellowFine}
                                onChange={(e) => setYellowFine(e.target.value)}
                                placeholder={t("createSanctionRuleForm.fields.yellowCardFinePlaceholder")}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>{t("createSanctionRuleForm.fields.doubleYellowFine")}</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                style={{ ...ui.input, ...ui.monosmall }}
                                value={doubleYellowFine}
                                onChange={(e) => setDoubleYellowFine(e.target.value)}
                                placeholder={t("createSanctionRuleForm.fields.doubleYellowFinePlaceholder")}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label style={ui.label}>{t("createSanctionRuleForm.fields.redCardFine")}</label>
                            <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                style={{ ...ui.input, ...ui.monosmall }}
                                value={redFine}
                                onChange={(e) => setRedFine(e.target.value)}
                                placeholder={t("createSanctionRuleForm.fields.redCardFinePlaceholder")}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div style={ui.footer}>
                <button
                    type="button"
                    style={{ ...ui.btn, ...ui.btnMuted }}
                    onClick={onClose}
                >
                    {t("createSanctionRuleForm.close")}
                </button>

                <button
                    type="submit"
                    style={{ ...ui.btn, ...ui.btnPrimary }}
                    disabled={uploading}
                >
                    {uploading ? t("createSanctionRuleForm.creating") : t("createSanctionRuleForm.create")}
                </button>
            </div>

            <style>{`
            .kbola-modal-lg {
              max-width: 920px !important;
            }

            form button:focus,
            form input:focus,
            form textarea:focus,
            form select:focus {
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            }

            .form-control {
              border-radius: 12px;
            }

            .form-check-input {
              cursor: pointer;
            }
      `}</style>
        </form>
    );
}